import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Input, Popover, Spin, Tooltip } from 'antd';
import { FilterFilled, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import type { ReferenceOption } from './types';

export interface ColumnFilterPopoverProps {
  /** Название колонки — в шапке поповера. */
  title: string;
  /** Полный список значений колонки: опции поля или записи справочника. */
  options: ReferenceOption[];
  loading?: boolean;
  /** Отмеченные значения; null — фильтра по колонке нет, показаны все. */
  selected: string[] | null;
  /** null — снять фильтр с колонки. */
  onApply: (values: string[] | null) => void;
  /** Вызывается при открытии — подгрузить значения справочника. */
  onOpen?: () => void;
}

/**
 * Фильтр по колонке прямо из её заголовка: список значений с галочками, как
 * автофильтр в Excel. Отличается от панели фильтров тем, что не надо выбирать
 * поле и оператор — только отметить нужные значения.
 */
export function ColumnFilterPopover({
  title,
  options,
  loading = false,
  selected,
  onApply,
  onOpen,
}: ColumnFilterPopoverProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Черновик выбора: правки применяются кнопкой, а не по каждому клику —
  // иначе таблица перезагружалась бы после каждой галочки.
  const [draft, setDraft] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setDraft(selected ?? options.map((o) => o.value));
    setSearch('');
  }, [open, selected, options]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q === '' ? options : options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const allChecked = draft.length === options.length && options.length > 0;
  const someChecked = draft.length > 0 && !allChecked;

  const toggle = (value: string) => {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const content = (
    <div style={{ width: 240 }}>
      <Input
        size="small"
        prefix={<SearchOutlined />}
        placeholder="Поиск значения"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 8 }}
        data-testid="column-filter-search"
      />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      ) : options.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет значений" />
      ) : (
        <>
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={(e) => setDraft(e.target.checked ? options.map((o) => o.value) : [])}
            style={{ marginBottom: 4 }}
          >
            Выбрать все
          </Checkbox>
          <div style={{ maxHeight: 240, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {shown.map((option) => (
              <Checkbox
                key={option.value}
                checked={draft.includes(option.value)}
                onChange={() => toggle(option.value)}
                data-testid={`column-filter-option-${option.value}`}
              >
                <span
                  style={{
                    display: 'inline-block',
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                  }}
                  title={option.label}
                >
                  {option.label}
                </span>
              </Checkbox>
            ))}
            {shown.length === 0 && (
              <span style={{ color: 'var(--lens-text-muted, #999)', fontSize: 12 }}>Ничего не найдено</span>
            )}
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          type="link"
          disabled={selected === null}
          onClick={() => {
            onApply(null);
            setOpen(false);
          }}
          style={{ marginRight: 'auto', paddingLeft: 0 }}
        >
          Сбросить
        </Button>
        <Button size="small" onClick={() => setOpen(false)}>
          Отмена
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={draft.length === 0}
          onClick={() => {
            // Отмечено всё — фильтр не нужен, снимаем его совсем.
            onApply(draft.length === options.length ? null : draft);
            setOpen(false);
          }}
          data-testid="column-filter-apply"
        >
          Применить
        </Button>
      </div>
    </div>
  );

  const active = selected !== null;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) onOpen?.();
      }}
      trigger="click"
      placement="bottomLeft"
      title={title}
      content={content}
      destroyOnHidden
    >
      <Tooltip title={active ? 'Фильтр по колонке включён' : 'Фильтр по колонке'}>
        <span
          className={`lens-col-filter${active ? ' lens-col-filter--active' : ''}`}
          onClick={(e) => e.stopPropagation()}
          role="button"
          aria-label={`Фильтр по колонке «${title}»`}
          data-testid="column-filter-trigger"
        >
          {active ? <FilterFilled /> : <FilterOutlined />}
        </span>
      </Tooltip>
    </Popover>
  );
}
